import {FALLBACK_LANGUAGE, i18n} from '@online-offer/i18n';
import {getOnlineOfferSessionId, type AlphaCatalogue} from '@online-offer/shared';

import {useLocation, useParams} from 'react-router-dom';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {useGetBuyingOnlineOfferQuery} from '../api/buyingOnlineOfferApi';
import {
  useGetAllApiEnumsQuery,
  useGetVehicleAuditQuery,
  VehicleAudit,
} from '../api/digitalCertificateApi';
import {
  useGetBusinessCaseOnlineOfferQuery,
  useGetOnlineOfferAlternativesQuery,
  useListLogosQuery,
} from '../api/onlineOfferApi';
import {OnlineOfferAlternativesResponseBody} from '../api/types/onlineOfferAlternativesTypes';
import {BusinessCaseOnlineOfferResponseBody} from '../api/types/onlineOfferTypes';
import {
  BUYING_ONLINE_OFFER_SESSION_ID_STORAGE_KEY_PREFIX,
  ONLINE_OFFER_SESSION_ID_STORAGE_KEY_PREFIX,
  queryParams,
} from '../consts/queryParams';
import {OfferDirection} from '../types/OfferDirection';
import {getOfferDirection} from '../utils/getOfferDirection';
import {getWorkspaceFromUri} from '../utils/getWorkspaceFromUri';
import {isOnlineOfferPdfRoute} from '../utils/isOnlineOfferPdfRoute';
import {localizeAlternativeCodes} from '../utils/localizeAlternativeCodes';
import {toOnlineOfferContext} from '../utils/toOnlineOfferContext';
import {useQueryState} from '../utils/useQueryState';

const {workspace} = getWorkspaceFromUri();

type GetOnlineOfferDataContext = Partial<BusinessCaseOnlineOfferResponseBody> & {
  alphaCatalogue: AlphaCatalogue | undefined;
  vehicleAudit: VehicleAudit | Nullish;
  tenantLogoUrl: string | Nullish;
  direction: OfferDirection;
  sessionId: string | Nullish;
  isPdfRoute: boolean;
  isOfferLoaded: boolean;
  isOfferFetching: boolean;
  isOfferErrored: boolean;
  isOfferLoading: boolean;
  offerError: ReturnType<typeof useGetBusinessCaseOnlineOfferQuery>['error'];
  isAlphaCatalogueFetching: boolean;
  isAlphaCatalogueErrored: boolean;
  isVehicleAuditFetching: boolean;
  isVehicleAuditErrored: boolean;
  carAuditId: string | Nullish;
  isListLogosFetching: boolean;
  /** Offer alternatives; `null` for a legacy offer, a buying offer, or when the call fails. */
  offerAlternatives: OnlineOfferAlternativesResponseBody | null;
  isOfferAlternativesLoading: boolean;
};

const logosKeys = {
  digitalDeal: 'tenant-logo_digital-deal',
  document: 'tenant-logo_document',
  email: 'tenant-logo_email',
};

export function useGetOnlineOfferData(): GetOnlineOfferDataContext {
  const [openedBy] = useQueryState(queryParams.OPENED_BY);
  const params = useParams<{offerId: string}>();
  const location = useLocation();
  const direction = getOfferDirection(location.pathname);
  const isBuying = direction === 'BUYING';
  const isPdfRoute = isOnlineOfferPdfRoute(location.pathname);
  const sessionId = isPdfRoute
    ? null
    : getOnlineOfferSessionId({
        offerId: params.offerId ?? '',
        openedBy: openedBy ?? null,
        storageKeyPrefix: isBuying
          ? BUYING_ONLINE_OFFER_SESSION_ID_STORAGE_KEY_PREFIX
          : ONLINE_OFFER_SESSION_ID_STORAGE_KEY_PREFIX,
      });

  const {data: listLogos, isFetching: isListLogosFetching} = useListLogosQuery(
    {'X-Workspace': workspace ?? ''},
    {skip: isNilOrEmpty(workspace)}
  );

  const logoUploadedByTenant = listLogos?.logos?.[logosKeys.digitalDeal]?.uri;

  const offerQueryArg = {
    offerId: params.offerId ?? '',
    workspace: workspace ?? '',
    openedBy: openedBy ?? null,
    sessionId,
  };

  const {
    data: sellingOffer,
    isError: isSellingOfferErrored,
    isFetching: isSellingOfferFetching,
    isLoading: isSellingOfferLoading,
    error: sellingOfferError,
  } = useGetBusinessCaseOnlineOfferQuery(offerQueryArg, {skip: isBuying});

  const {
    data: buyingOffer,
    isError: isBuyingOfferErrored,
    isFetching: isBuyingOfferFetching,
    isLoading: isBuyingOfferLoading,
    error: buyingOfferError,
  } = useGetBuyingOnlineOfferQuery(offerQueryArg, {skip: !isBuying});

  // Legacy offers have no alternatives. A failing call (e.g. a backend that does not serve the
  // endpoint yet) must not break them, so errors read as "no alternatives". The customer always
  // sees the latest revision: an open page refreshes on focus and reconnect, like financing.
  const {data: alternativesResponse, isLoading: isOfferAlternativesLoading} =
    useGetOnlineOfferAlternativesQuery(
      {
        offerId: params.offerId ?? '',
        workspace: workspace ?? '',
        language: i18n.resolvedLanguage ?? FALLBACK_LANGUAGE,
      },
      {
        skip: isBuying || isNilOrEmpty(params.offerId),
        refetchOnFocus: true,
        refetchOnReconnect: true,
      }
    );
  const buyingOfferContext = isNotNil(buyingOffer) ? toOnlineOfferContext(buyingOffer) : undefined;
  const offer = isBuying ? buyingOfferContext : sellingOffer;
  const isOfferLoaded = isBuying ? isNotNil(buyingOffer) : isNotNil(sellingOffer);
  const isOfferFetching = isBuying ? isBuyingOfferFetching : isSellingOfferFetching;
  const isOfferErrored = isBuying ? isBuyingOfferErrored : isSellingOfferErrored;
  const isOfferLoading = isBuying ? isBuyingOfferLoading : isSellingOfferLoading;
  const offerError = isBuying ? buyingOfferError : sellingOfferError;

  const {
    data: alphaCatalogue,
    isError: isAlphaCatalogueErrored,
    isFetching: isAlphaCatalogueFetching,
  } = useGetAllApiEnumsQuery(
    {
      lang: [i18n.resolvedLanguage ?? FALLBACK_LANGUAGE],
      vehicleType: offer?.vehicle?.type ? [offer.vehicle.type] : undefined,
    },
    {skip: isOfferLoading}
  );

  // Catalogue codes of stock alternatives read like the legacy sections once the catalogue is in.
  const offerAlternatives =
    isNotNil(alternativesResponse) && alternativesResponse.alternatives.length > 0
      ? localizeAlternativeCodes(alternativesResponse, alphaCatalogue)
      : null;

  // Fetch digital certificate only if certificate(carAuditId) is present.
  // The buying payload carries no audit, so this always skips for that direction.
  const {
    data: vehicleAudit,
    isError: isVehicleAuditErrored,
    isFetching: isVehicleAuditFetching,
  } = useGetVehicleAuditQuery(
    {auditId: sellingOffer?.carAuditId ?? '', workspace: workspace ?? ''},
    {skip: isNilOrEmpty(sellingOffer?.carAuditId)}
  );

  return {
    ...offer,
    alphaCatalogue,
    vehicleAudit,
    tenantLogoUrl: logoUploadedByTenant,
    direction,
    sessionId,
    isPdfRoute,
    isOfferLoaded,
    isOfferFetching,
    isOfferErrored,
    isOfferLoading,
    offerError,
    isAlphaCatalogueFetching,
    isAlphaCatalogueErrored,
    isVehicleAuditFetching,
    isVehicleAuditErrored,
    carAuditId: sellingOffer?.carAuditId,
    isListLogosFetching,
    offerAlternatives,
    isOfferAlternativesLoading,
  };
}
